import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { buildSpinCaption } from '../lib/spinCaptions';

// Spin Film 360 (Pocket) — V1
// ────────────────────────────────────────────────────────────────────────
// Standalone product at /spin. SAME backend engine as the event Booth
// (`POST /api/spin/upload` → reuses `_render_360_boomerang`), but
// completely different UX: full-bleed camera, single record button, no
// instructional UI, no "performance framing", no QR / event context.
// Per the V2 brief: "Booth = a moment", "Pocket = fun".
//
// Flow: open → camera (preview live) → tap record → record up to 12s →
// auto-process → instant reveal. Nothing else.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recording duration. 10s gives a smooth boomerang loop without
// dragging — confirmed with the user as the canonical length.
// Single source of truth — DO NOT introduce a second constant for
// "default" vs "max" durations; the timer reads ONLY from this.
const POCKET_RECORD_SECONDS = 10;
const POLL_INTERVAL_MS = 1500;
// Transient 5xx/network errors tolerated before surfacing to the user.
// Production origin can briefly 520 while the worker warms up / restarts
// — we don't want to show "lost connection" on every blink.
const POLL_TRANSIENT_RETRY_CAP = 8;
// Total poll budget — production CPU is dramatically slower than preview
// (we measured 30s for a 4s test clip on the prod container vs ~3s in
// preview). 120s gives a comfortable safety margin so transient cell
// blips don't tip a successfully-rendering video into a false-fail.
const POLL_TOTAL_TIMEOUT_MS = 120_000;

const SpinPocketPage = () => {
  // phase: idle | recording | processing | playback | failed
  // 'failed' is shown when upload/render hits a hard error — gives the
  // guest an explicit "Try again" CTA instead of silently bouncing
  // them back to the idle/start screen (which felt like the app had
  // forgotten the recording).
  const [phase, setPhase] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [spinId, setSpinId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [respinTransition, setRespinTransition] = useState(false);  // 300ms "Get ready…" curtain after Respin tap

  const previewRef = useRef(null);
  const playbackRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  // Phase guard — the cleanup useEffect MUST NOT stop the camera
  // stream while a recording is in flight. We read the live phase
  // through a ref so the cleanup closure (which captures the value at
  // mount time) can see the current value.
  const phaseRef = useRef('idle');
  useEffect(() => {
    phaseRef.current = phase;
    // Lightweight diagnostic — production-safe (no PII).
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.log('[spin-pocket] phase →', phase);
    }
  }, [phase]);

  // ── Bulletproof camera (re-)init ───────────────────────────────────
  // Always stop the previous stream, re-request, re-attach. Used on
  // initial mount AND on respin — the FullBleedCamera unmounts during
  // playback so on respin we get a brand-new <video> element that has
  // NO srcObject attached. Reusing the old stream silently produces a
  // black screen. Per the user's brief: "fully reset on respin, do not
  // try to reuse the old stream". Wrapped in retry-on-mount-not-ready
  // for the race where setState → render → ref mount hasn't completed.
  const startCamera = useCallback(async () => {
    // 1. Tear down whatever stream is currently in flight
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
    }
    if (previewRef.current && previewRef.current.srcObject) {
      try {
        previewRef.current.srcObject.getTracks().forEach((t) => t.stop());
      } catch {}
      previewRef.current.srcObject = null;
    }

    // 2. Re-request camera
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1080 }, height: { ideal: 1080 } },
        audio: false,
      });
    } catch (e) {
      console.error('[spin-pocket] camera blocked', e);
      setError('Camera needed. Allow access and reload.');
      return null;
    }
    streamRef.current = stream;
    setCameraReady(true);

    // 3. Attach to the <video> element. The element may not be mounted
    // YET if startCamera was called immediately after setPhase('idle')
    // — wait up to ~3 frames for React to mount it before giving up.
    const attach = async () => {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        if (previewRef.current) {
          previewRef.current.srcObject = stream;
          // 4. Force play (mobile Safari needs this)
          try { await previewRef.current.play(); } catch {}
          // 5. Tiny delayed re-play kick — Safari sometimes drops the
          // first play() if the element was just mounted.
          setTimeout(() => {
            if (previewRef.current && previewRef.current.paused) {
              previewRef.current.play().catch(() => {});
            }
          }, 120);
          return true;
        }
        // requestAnimationFrame would be ideal but a small setTimeout
        // works in every browser including jsdom for tests.
        await new Promise((r) => setTimeout(r, 50));
      }
      return false;
    };
    await attach();
    return stream;
  }, []);

  // ── Acquire camera as soon as the page loads ───────────────────────
  // Pocket's whole vibe is "open the app, you're in" — so we skip the
  // explicit "Start" button. The first frame is already live preview.
  useEffect(() => {
    let active = true;
    (async () => {
      const stream = await startCamera();
      if (!active && stream) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        streamRef.current = null;
      }
    })();
    return () => {
      active = false;
      // CRITICAL: never stop the camera stream while a recording is
      // actually in flight. The page can re-run cleanup if React
      // strict-mode double-invokes effects, or if the dep changes.
      // Tearing down the stream here was the root cause of the "camera
      // goes black mid-record + recording continues in background"
      // regression. We only stop if we're truly leaving the page or
      // already idle.
      const live = phaseRef.current;
      if (live === 'recording' || live === 'processing') {
        // Diagnostic — should never fire in normal use.
        // eslint-disable-next-line no-console
        console.warn('[spin-pocket] cleanup skipped — phase=', live);
        return;
      }
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        streamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [startCamera]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // ── Tap record / tap stop ──────────────────────────────────────────
  const startRecording = useCallback(() => {
    if (!streamRef.current || !streamRef.current.active) {
      // eslint-disable-next-line no-console
      console.warn('[spin-pocket] startRecording — no active stream');
      setError('Camera lost connection — tap again.');
      return;
    }
    setError(null);
    setSeconds(0);
    chunksRef.current = [];
    try {
      const mime = ['video/mp4;codecs=h264', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']
        .find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'video/mp4' });
        // Lightweight diagnostic.
        // eslint-disable-next-line no-console
        console.log('[spin-pocket] recorder onstop, blob bytes=', blob.size);
        // Transition off recording first so guest never gets stuck on
        // a stale state if uploadClip fails or returns early.
        setPhase('processing');
        await uploadClip(blob, rec.mimeType || 'video/mp4');
      };
      rec.start();
      // eslint-disable-next-line no-console
      console.log('[spin-pocket] recorder started, state=', rec.state, 'mime=', mime);
      setPhase('recording');

      let s = 0;
      timerRef.current = setInterval(() => {
        s += 1;
        setSeconds(s);
        if (s >= POCKET_RECORD_SECONDS) {
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          try {
            if (rec.state !== 'inactive') {
              rec.stop();   // fires onstop → phase→processing
            } else {
              // Recorder went inactive without onstop firing — bail
              // out to the failed screen (NOT idle) so the guest can
              // retry rather than feel like the app silently reset.
              setError('Recording stopped early — please try again.');
              setPhase('failed');
            }
          } catch {}
        }
      }, 1000);
    } catch (e) {
      console.error('[spin-pocket] record start failed', e);
      setError('Recording failed. Try again.');
      setPhase('failed');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();   // fires onstop → phase→processing
        return;
      }
    } catch {}
    // Recorder was never active — show failed screen (NOT idle) so
    // the guest gets an explicit retry rather than feeling like the
    // app silently reset.
    setError("Recording didn't capture — please try again.");
    setPhase('failed');
  }, []);

  const uploadClip = useCallback(async (blob, mime) => {
    setPhase('processing');
    try {
      const fd = new FormData();
      fd.append('file', blob, mime.includes('webm') ? 'spin.webm' : 'spin.mp4');
      const r = await axios.post(`${API}/spin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const id = r.data?.spin_id;
      if (!id) throw new Error('no spin_id');
      setSpinId(id);

      const startedAt = Date.now();
      let transientErrors = 0;   // swallow brief 5xx / network blips
      const tick = async () => {
        try {
          const sr = await axios.get(`${API}/spin/${id}`, { timeout: 8000 });
          transientErrors = 0;   // reset on any successful poll
          if (sr.data?.status === 'ready' && sr.data.video_url) {
            setVideoUrl(sr.data.video_url);
            setPhase('playback');
            return;
          }
          if (sr.data?.status === 'failed') {
            setError(sr.data?.error || 'Could not process. Try again.');
            setPhase('failed');
            return;
          }
          if (Date.now() - startedAt > POLL_TOTAL_TIMEOUT_MS) {
            setError('Still creating your video — try again in a moment.');
            setPhase('failed');
            return;
          }
          setTimeout(tick, POLL_INTERVAL_MS);
        } catch (pollErr) {
          // Transient origin-restart / edge 5xx / brief network blip —
          // do NOT show "lost connection" after the first hiccup. The
          // render is probably still in flight. Keep retrying silently
          // up to POLL_TRANSIENT_RETRY_CAP consecutive misses, then
          // surface a soft message (not a scary one).
          transientErrors += 1;
          // eslint-disable-next-line no-console
          console.warn(`[spin-pocket] poll error ${transientErrors}/${POLL_TRANSIENT_RETRY_CAP}`, pollErr?.response?.status || pollErr?.message);
          if (Date.now() - startedAt > POLL_TOTAL_TIMEOUT_MS) {
            setError('Still creating your video — try again in a moment.');
            setPhase('failed');
            return;
          }
          if (transientErrors >= POLL_TRANSIENT_RETRY_CAP) {
            setError('Still creating your video — try again in a moment.');
            setPhase('failed');
            return;
          }
          // Keep polling — back off a little on repeated errors.
          setTimeout(tick, POLL_INTERVAL_MS + (transientErrors * 300));
        }
      };
      setTimeout(tick, POLL_INTERVAL_MS);
    } catch (e) {
      console.error('[spin-pocket] upload failed', e);
      setError(e?.response?.data?.detail || 'Upload failed.');
      setPhase('failed');
    }
  }, []);

  // ── Reset for another spin ─────────────────────────────────────────
  // ── Respin — fully reset camera + UI ───────────────────────────────
  // Per the user's brief: do NOT try to reuse the old stream. Use the
  // bulletproof `startCamera()` that stops, re-requests, re-attaches.
  // The 300ms "Get ready…" curtain hides the camera flicker so the
  // reset feels intentional, not broken.
  const resetForAnother = useCallback(async () => {
    setRevealed(false);
    setRespinTransition(true);  // 300ms "Get ready…" curtain
    setVideoUrl(null);
    setSpinId(null);
    setError(null);
    setSeconds(0);

    // Switch back to idle so FullBleedCamera mounts a fresh <video>.
    setPhase('idle');

    // Start the camera against the freshly-mounted element. The helper
    // retries the attach for up to ~300ms while React mounts the ref.
    await startCamera();

    // Drop the curtain after a tiny beat so the user sees the live
    // preview, not a black flicker.
    setTimeout(() => setRespinTransition(false), 300);
  }, [startCamera]);

  // ── Reveal-moment timer (V2: 1.6s no-UI hold on playback) ─────────
  useEffect(() => {
    if (phase !== 'playback') return;
    const t = setTimeout(() => setRevealed(true), 1600);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div
      data-testid="spin-pocket-page"
      style={{
        minHeight: '100svh',
        background: '#000',
        color: '#FFFFFF',
        fontFamily: 'Helvetica, Arial, sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Live preview is mounted during idle + recording AND processing
          phases. Keeping it visible in 'processing' too means there's
          no black-screen blink between the recorder firing onstop and
          the upload starting — the camera fades out via the processing
          curtain instead. */}
      {(phase === 'idle' || phase === 'recording') && (
        <FullBleedCamera
          previewRef={previewRef}
          cameraReady={cameraReady}
          phase={phase}
          seconds={seconds}
          onRecord={startRecording}
          onStop={stopRecording}
          error={error}
        />
      )}

      <AnimatePresence>
        {phase === 'processing' && <PocketProcessing key="proc" />}
        {phase === 'failed' && (
          <PocketFailed
            key="failed"
            error={error}
            onRetry={resetForAnother}
            data-testid="spin-pocket-failed"
          />
        )}
        {phase === 'playback' && videoUrl && (
          <PocketPlayback
            key="pb"
            playbackRef={playbackRef}
            videoUrl={videoUrl}
            revealed={revealed}
            onAnother={resetForAnother}
          />
        )}
        {respinTransition && (
          <motion.div
            key="respin-curtain"
            data-testid="spin-pocket-respin-curtain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{
              position: 'fixed', inset: 0, zIndex: 60,
              background: '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Georgia, serif',
              fontSize: 22, letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.86)',
            }}
          >
            ✦ Get ready…
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Full-bleed camera with single circular record button ───────────────

const FullBleedCamera = ({ previewRef, cameraReady, phase, seconds, onRecord, onStop, error }) => (
  <div style={{ position: 'fixed', inset: 0, background: '#000' }}>
    <video
      ref={previewRef}
      playsInline
      muted
      autoPlay
      data-testid="spin-pocket-preview"
      style={{
        width: '100%', height: '100%',
        objectFit: 'cover',
        transform: 'scaleX(-1)',
        background: '#000',
      }}
    />

    {/* Subtle vignette so the record button reads cleanly */}
    <div style={{
      position: 'absolute', inset: 0,
      pointerEvents: 'none',
      background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)',
    }} />

    {/* Top status bar — Event Visibility: REC pip + centred timer */}
    <div style={{
      position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', left: 0, right: 0,
      padding: '0 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'none',
    }}>
      {phase === 'recording' ? (
        <>
          {/* REC pip — left, large with strong glow */}
          <div style={{
            position: 'absolute', left: 16, top: 0,
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(0,0,0,0.7)', padding: '10px 16px',
            borderRadius: 999, backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: 999, background: '#FF3B5C',
              boxShadow: '0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset',
              animation: 'spinRecPulse 1.1s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.14em' }}>REC</span>
          </div>
          {/* Timer — centred, large, high contrast */}
          <span data-testid="spin-pocket-timer" style={{
            background: 'rgba(0,0,0,0.7)', padding: '10px 18px',
            borderRadius: 999, fontSize: 20, fontWeight: 800,
            letterSpacing: '-0.01em',
            fontVariantNumeric: 'tabular-nums', backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            color: '#FFFFFF',
          }}>
            0:{String(seconds).padStart(2, '0')} / 0:{String(POCKET_RECORD_SECONDS).padStart(2, '0')}
          </span>
          {/* Slow-motion cue — appears at 7s mark per motion spec v2.
              Tells guests "react NOW, this is the hero moment". */}
          {seconds >= 7 && seconds < 10 && (
            <motion.div
              key="slowmo-cue"
              data-testid="spin-pocket-slowmo-cue"
              initial={{ opacity: 0, scale: 0.85, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }}
              transition={{ type: 'spring', stiffness: 320, damping: 22 }}
              style={{
                position: 'absolute',
                top: 'calc(env(safe-area-inset-top, 0px) + 80px)',
                left: '50%', transform: 'translateX(-50%)',
                fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em',
                color: '#FFFFFF',
                background: 'linear-gradient(135deg, rgba(212,175,94,0.35) 0%, rgba(184,151,98,0.35) 100%)',
                padding: '14px 24px', borderRadius: 999,
                backdropFilter: 'blur(18px)',
                whiteSpace: 'nowrap',
                border: '1.5px solid rgba(212,175,94,0.8)',
                boxShadow: '0 0 28px rgba(212,175,94,0.45), 0 6px 24px rgba(0,0,0,0.45)',
              }}
            >
              ✨ Slow motion starting…
            </motion.div>
          )}
        </>
      ) : (
        <span style={{
          position: 'absolute', left: 18, top: 14,
          fontSize: 13, fontWeight: 700, letterSpacing: '0.22em',
          color: 'rgba(255,255,255,0.92)',
          textTransform: 'uppercase',
          textShadow: '0 1px 8px rgba(0,0,0,0.7)',
        }}>
          ✦ Spin
        </span>
      )}
    </div>

    {/* Single record button — Event Visibility: bigger (96px), gradient
        ring, brighter inner dot, lifted further from the nav bar. */}
    <div style={{
      position: 'absolute',
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 48px)',
      left: 0, right: 0,
      display: 'flex', justifyContent: 'center',
    }}>
      <button
        data-testid={phase === 'recording' ? 'spin-pocket-stop-btn' : 'spin-pocket-record-btn'}
        onClick={phase === 'recording' ? onStop : onRecord}
        disabled={!cameraReady && phase === 'idle'}
        aria-label={phase === 'recording' ? 'Stop recording' : 'Start recording'}
        style={{
          width: 96, height: 96,
          borderRadius: '50%',
          border: '5px solid rgba(255,255,255,1)',
          background: 'rgba(0,0,0,0.25)',
          padding: 0,
          cursor: cameraReady ? 'pointer' : 'wait',
          opacity: cameraReady ? 1 : 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.15s ease',
          WebkitTapHighlightColor: 'transparent',
          boxShadow: '0 16px 40px rgba(255,59,92,0.5), 0 0 0 1px rgba(0,0,0,0.4)',
        }}
      >
        <motion.div
          animate={
            phase === 'recording'
              ? { borderRadius: 10, width: 36, height: 36, background: '#FFFFFF' }
              : { borderRadius: '50%', width: 70, height: 70, background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)' }
          }
          transition={{ duration: 0.25, ease: 'easeOut' }}
        />
      </button>
    </div>

    {/* No camera permission yet */}
    {!cameraReady && phase === 'idle' && (
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 12,
        color: 'rgba(255,255,255,0.7)', fontSize: 14,
      }}>
        <div style={{
          width: 28, height: 28,
          border: '2px solid rgba(255,255,255,0.2)',
          borderTopColor: '#D4AF37', borderRadius: '50%',
          animation: 'spinRotate 1.5s linear infinite',
        }} />
        Allow camera access…
      </div>
    )}

    {error && (
      <div data-testid="spin-pocket-error" style={{
        position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(255,138,138,0.14)', color: '#FFD8D8',
        border: '1px solid rgba(255,138,138,0.3)',
        padding: '10px 14px', borderRadius: 12,
        fontSize: 12, maxWidth: '90%', textAlign: 'center',
      }}>
        {error}
      </div>
    )}

    <style>{`
      @keyframes spinRotate { to { transform: rotate(360deg); } }
      @keyframes spinRecPulse {
        0%, 100% { box-shadow: 0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1); }
        50% { box-shadow: 0 0 28px rgba(255,59,92,1), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1.12); }
      }
    `}</style>
  </div>
);

// ── Failed — gives the guest a clear retry instead of a silent reset ──
//
// This screen exists specifically because the previous flow used
// `setPhase('idle')` on upload/poll failures, which silently sent the
// guest back to the start screen — making it look like nothing had
// happened. Per the P0 brief: "DO NOT return to start unless explicit
// user cancel OR fatal upload failure". When something does fail, we
// own the failure with an honest message + a single tap to start over.
const PocketFailed = ({ error, onRetry }) => (
  <motion.div
    data-testid="spin-pocket-failed"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: '#000',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24, padding: '32px 24px', color: '#FFFFFF',
    }}
  >
    <div style={{
      width: 60, height: 60, borderRadius: '50%',
      border: '1.5px solid rgba(255,138,138,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 26, color: '#FFB4B4',
    }}>!</div>
    <p style={{
      margin: 0, fontFamily: 'Georgia, serif', fontSize: 22,
      letterSpacing: '0.04em', textAlign: 'center', maxWidth: 320,
      color: 'rgba(255,255,255,0.92)',
    }}>
      That didn't quite spin.
    </p>
    <p
      data-testid="spin-pocket-failed-message"
      style={{
        margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.55)',
        maxWidth: 320, textAlign: 'center', lineHeight: 1.55,
      }}
    >
      {error || 'Something interrupted the render.'}
    </p>
    <button
      data-testid="spin-pocket-retry-btn"
      onClick={onRetry}
      style={{
        marginTop: 8,
        padding: '14px 32px', borderRadius: 999,
        background: '#FFFFFF', color: '#000000',
        border: 'none', fontSize: 15, fontWeight: 700,
        letterSpacing: '0.05em', cursor: 'pointer',
        boxShadow: '0 12px 28px rgba(255,255,255,0.2)',
      }}
    >
      Try again
    </button>
  </motion.div>
);

// ── Processing — minimal, no instructional copy ───────────────────────

const PocketProcessing = () => {
  // Production render takes 15-30s end-to-end. Without progressive copy
  // the user sees a static "Spinning the moment…" for half a minute which
  // reads as broken. We rotate through 3 phase-appropriate lines so the
  // wait feels intentional, not stuck.
  const lines = [
    '\u2728 Spinning the moment\u2026',
    '\u2728 Painting the slow-mo\u2026',
    '\u2728 Sealing your share-ready cut\u2026',
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % lines.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        position: 'fixed', inset: 0, background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 22, padding: 24, textAlign: 'center',
      }}
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 4.8, ease: 'linear', repeat: Infinity }}
        style={{
          width: 56, height: 56,
          border: '2px solid rgba(255,255,255,0.08)',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
        }}
      />
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.45 }}
          data-testid="spin-pocket-processing-title"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 22, fontWeight: 400, margin: 0, color: '#FFFFFF',
          }}
        >
          {lines[idx]}
        </motion.p>
      </AnimatePresence>
      <p
        style={{
          margin: 0, fontSize: 13, letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.42)',
        }}
      >
        Hold tight \u2014 a few seconds.
      </p>
    </motion.div>
  );
};

// ── Playback — same delayed-reveal pattern as Booth V2 ────────────────

const PocketPlayback = ({ playbackRef, videoUrl, revealed, onAnother }) => {
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  // SpinPocket has no event context (standalone /spin), so the caption
  // falls through to the generic "Caught in cinematic slow-motion ✨"
  // hook. The video URL is appended so paste-anywhere shares carry the
  // playback link.
  const captionText = buildSpinCaption({ platform: 'generic' });
  const captionWithUrl = videoUrl ? `${captionText}\n${videoUrl}` : captionText;
  const onShare = useCallback(async () => {
    if (!videoUrl) return;

    // ── Web Share API Level 2 — share the VIDEO FILE itself ──────────
    // Sharing the file (not just the URL) is what unlocks TikTok /
    // Instagram / Reels in the iOS + Android native share sheet.
    if (navigator.canShare && typeof navigator.share === 'function') {
      try {
        const resp = await fetch(videoUrl, { mode: 'cors' });
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], 'spin-film-360.mp4', { type: 'video/mp4' });
          const payload = { files: [file], title: 'Spin Film 360', text: '✦ A spin' };
          if (navigator.canShare(payload)) {
            await navigator.share(payload);
            return;
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[spin-pocket] file share fallback', err);
      }
    }

    // Fallback 1: URL-only share
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Spin', text: '✦ A spin', url: videoUrl });
        return;
      } catch {}
    }
    // Fallback 2: clipboard
    try {
      await navigator.clipboard.writeText(videoUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  }, [videoUrl]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed', inset: 0, background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 18, padding: '24px 16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.05, type: 'spring', stiffness: 220, damping: 22 }}
        style={{
          width: '100%', maxWidth: 460,
          aspectRatio: '1 / 1',
          borderRadius: 22, overflow: 'hidden',
          background: '#0F0F14',
          boxShadow: '0 40px 90px rgba(0,0,0,0.65), 0 0 0 1px rgba(212,175,55,0.18)',
          position: 'relative',
        }}
      >
        <video
          ref={playbackRef}
          src={videoUrl}
          autoPlay
          loop
          playsInline
          controls={revealed}
          data-testid="spin-pocket-playback-video"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <AnimatePresence>
          {revealed && (
            <motion.div
              key="top"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                padding: '14px 14px 28px', textAlign: 'center',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)',
                pointerEvents: 'none',
              }}
            >
              <p style={{
                margin: 0, color: '#FFFFFF',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 16, fontWeight: 400, letterSpacing: '-0.005em',
              }}>
                ✦ Your spin
              </p>
              <p
                data-testid="spin-pocket-format-tag"
                style={{
                  margin: '4px 0 0', color: 'rgba(212,175,94,0.92)',
                  fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                }}
              >
                ✨ Cinematic slow-motion reel
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {revealed && (
          <motion.div
            key="cta-cluster"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 14,
            }}
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                data-testid="spin-pocket-share-btn"
                onClick={onShare}
                style={{
                  padding: '13px 22px', background: '#FFFFFF', color: '#0A0A0F',
                  border: 'none', borderRadius: 999, fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', letterSpacing: '0.04em',
                }}
              >
                {copied ? '✓ Copied' : '↗ Share'}
              </button>
              <a
                data-testid="spin-pocket-download-btn"
                href={videoUrl}
                download={`spin-${Date.now()}.mp4`}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: '13px 22px', background: 'transparent', color: '#FFFFFF',
                  border: '1px solid rgba(255,255,255,0.32)',
                  borderRadius: 999, fontSize: 13, fontWeight: 700,
                  textDecoration: 'none', letterSpacing: '0.04em',
                }}
              >
                ↓ Download
              </a>
              <button
                data-testid="spin-pocket-another-btn"
                onClick={onAnother}
                style={{
                  padding: '13px 22px', background: 'transparent',
                  color: 'rgba(255,255,255,0.6)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 999, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', letterSpacing: '0.04em',
                }}
              >
                Spin again
              </button>
            </div>

            {/* PLATFORM-AWARE CAPTION — pre-builds the social caption
                so guests can one-tap copy + paste into TikTok / Reels.
                Pocket has no event context so uses the generic hook. */}
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 8, width: '100%', maxWidth: 320,
            }}>
              <div
                data-testid="spin-pocket-caption-preview"
                style={{
                  width: '100%', padding: '10px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, color: 'rgba(255,255,255,0.86)',
                  fontSize: 12, lineHeight: 1.45, whiteSpace: 'pre-line',
                  textAlign: 'left', fontWeight: 500,
                }}
              >
                {captionWithUrl}
              </div>
              <button
                data-testid="spin-pocket-copy-caption"
                onClick={async () => {
                  try {
                    if (navigator.clipboard) await navigator.clipboard.writeText(captionWithUrl);
                    setCaptionCopied(true);
                    setTimeout(() => setCaptionCopied(false), 2200);
                  } catch { /* ignore */ }
                }}
                style={{
                  padding: '10px 20px',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: captionCopied
                    ? 'linear-gradient(135deg, rgba(0,212,255,0.3) 0%, rgba(184,151,98,0.3) 100%)'
                    : 'linear-gradient(135deg, rgba(212,175,94,0.22) 0%, rgba(184,151,98,0.22) 100%)',
                  color: '#FFFFFF',
                  border: `1px solid ${captionCopied ? 'rgba(0,212,255,0.65)' : 'rgba(212,175,94,0.5)'}`,
                  borderRadius: 999, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', letterSpacing: '0.02em',
                }}
              >
                {captionCopied ? '✓ Caption copied' : '📋 Copy caption'}
              </button>
            </div>

            <a
              data-testid="spin-pocket-host-cta"
              href="https://eventdrop.co"
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 11, color: '#D4AF37',
                letterSpacing: '0.18em', textTransform: 'uppercase',
                textDecoration: 'underline', textUnderlineOffset: 5,
                marginTop: 2,
              }}
            >
              Want this at your event?
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SpinPocketPage;
